import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  AppState,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as DocumentPicker from 'expo-document-picker';
import { storage } from '../../utils/storage';
import { AppLayout } from '../../components/layout/AppLayout';
import { supabase } from '../../api/supabase';
import theme from '../../theme';

interface ImportProgress {
  phase: 'idle' | 'uploading' | 'processing' | 'complete' | 'error';
  currentStep: number;
  totalSteps: number;
  message: string;
  details?: string;
  processed?: number;
  total?: number;
  sessionId?: string;
}

interface ImportStats {
  totalRecords: number;
  vehicleMatches: number;
  plateMatches: number;
  notFound: number;
  totalAmount: number;
  accuracy: number;
  duplicatesRemoved: number;
  dateRange?: string;
  batchId?: string;
  sourceFile?: string;
  importType?: string;
  fileHash?: string;
  processedRecords?: number;
  rawRecordsSaved?: number;
}

export const SalikImportScreenFixed = () => {
  const navigation = useNavigation();
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [progress, setProgress] = useState<ImportProgress>({
    phase: 'idle',
    currentStep: 0,
    totalSteps: 6,
    message: 'Ready to import Salik toll data',
  });
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const appState = useRef(AppState.currentState);

  // Generate unique session ID for this import session
  useEffect(() => {
    const newSessionId = `salik_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    loadSessionState(newSessionId);
  }, []);

  // Handle app state changes (background/foreground)
  useEffect(() => {
    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => subscription?.remove();
  }, []);

  const handleAppStateChange = (nextAppState: string) => {
    if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
      if (isProcessing) {
        restoreProcessingState();
      }
    } else if (nextAppState.match(/inactive|background/) && isProcessing) {
      saveSessionState();
    }
    appState.current = nextAppState;
  };

  const saveSessionState = async () => {
    try {
      // Don't save the file content to prevent storage quota issues
      const fileMetadata = selectedFile ? {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
        lastModified: selectedFile.lastModified
      } : null;
      
      const state = {
        selectedFile: fileMetadata, // Only save metadata, not content
        progress,
        importStats,
        isProcessing,
        sessionId,
        timestamp: Date.now()
      };
      await storage.setItem(`salik_import_${sessionId}`, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save session state:', error);
    }
  };

  const loadSessionState = async (sid: string) => {
    try {
      const savedState = await storage.getItem(`salik_import_${sid}`);
      if (savedState) {
        const state = JSON.parse(savedState);
        if (Date.now() - state.timestamp < 30 * 60 * 1000) {
          // Only restore metadata, user will need to re-select file
          if (state.selectedFile && typeof state.selectedFile === 'object' && state.selectedFile.name) {
            // This is metadata, not the full file
            console.log(`Previous session found with file: ${state.selectedFile.name}`);
          }
          setProgress(state.progress);
          setImportStats(state.importStats);
          setIsProcessing(state.isProcessing);
        } else {
          await storage.removeItem(`salik_import_${sid}`);
        }
      }
    } catch (error) {
      console.warn('Failed to load session state:', error);
    }
  };

  const restoreProcessingState = async () => {
    try {
      if (progress.sessionId) {
        const { data: batchStatus } = await supabase
          .from('accounting_transactions')
          .select('id, created_at')
          .eq('category', 'salik')
          .eq('import_batch_id', progress.sessionId)
          .order('created_at', { ascending: false })
          .limit(1);

        if (batchStatus && batchStatus.length > 0) {
          setProgress({
            ...progress,
            phase: 'complete',
            message: 'Import completed while app was in background'
          });
          setIsProcessing(false);
        }
      }
    } catch (error) {
      console.warn('Failed to restore processing state:', error);
    }
  };

  const steps = [
    { id: 1, name: 'File Upload', description: 'Upload CSV file from Salik system' },
    { id: 2, name: 'File Validation', description: 'Validate file format and structure' },
    { id: 3, name: 'Duplicate Detection', description: 'Check for duplicate toll records' },
    { id: 4, name: 'Vehicle Matching', description: 'Match vehicles using plate numbers' },
    { id: 5, name: 'Toll Processing', description: 'Process toll charges with chunked batches' },
    { id: 6, name: 'Database Update', description: 'Save toll data to accounting system' },
  ];

  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        setSelectedFile(result.assets[0]);
        setProgress({
          phase: 'idle',
          currentStep: 0,
          totalSteps: 6,
          message: `Selected: ${result.assets[0].name}`,
          sessionId
        });
        setImportStats(null);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to pick file');
      console.error('File picker error:', error);
    }
  };

  const performOptimizedImport = async (skipDuplicateCheck = false) => {
    if (!selectedFile) {
      Alert.alert('Error', 'Please select a file first');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Step 1: File Upload & Reading
      setProgress({ 
        phase: 'uploading', 
        currentStep: 1, 
        totalSteps: 6, 
        message: 'Reading file...', 
        sessionId,
        details: 'Loading Salik CSV content into memory'
      });
      
      let content: string;
      if (selectedFile.uri) {
        const response = await fetch(selectedFile.uri);
        content = await response.text();
      } else {
        content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsText(selectedFile as any);
        });
      }
      
      // Step 2: File Validation
      setProgress({ 
        phase: 'processing', 
        currentStep: 2, 
        totalSteps: 6, 
        message: 'Validating file format...',
        sessionId,
        details: 'Checking CSV structure and Salik headers'
      });

      const lines = content.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        throw new Error('File appears to be empty or invalid');
      }

      const headers = lines[0].split(',');
      console.log('Salik CSV Headers:', headers);

      // Expected Salik headers: Plate Number, Toll Gate, Date, Time, Amount, etc.
      const requiredHeaders = ['plate', 'gate', 'date', 'amount'];
      const headerCheck = requiredHeaders.some(required => 
        headers.some(header => header.toLowerCase().includes(required.toLowerCase()))
      );

      if (!headerCheck) {
        throw new Error('Invalid Salik CSV format. Required columns: Plate Number, Toll Gate, Date, Amount');
      }

      // Step 3: Duplicate Detection (if not skipped)
      if (!skipDuplicateCheck) {
        setProgress({ 
          phase: 'processing', 
          currentStep: 3, 
          totalSteps: 6, 
          message: 'Checking for duplicates...',
          sessionId,
          details: 'Verifying against existing toll records'
        });

        const duplicateResult = await checkForDuplicatesOptimized(lines);
        if (duplicateResult.hasDuplicates) {
          setIsProcessing(false);
          
          if (duplicateResult.isDuplicateFile) {
            Alert.alert(
              'Duplicate File Detected',
              `This Salik file was already imported. Records: ${duplicateResult.duplicateCount}`,
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'Force Re-import', onPress: () => performOptimizedImport(true) }
              ]
            );
            return;
          } else {
            Alert.alert(
              'Potential Duplicates',
              `Found ${duplicateResult.duplicateCount} potential duplicate toll records.`,
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'Continue', onPress: () => processDataChunked(lines) }
              ]
            );
            return;
          }
        }
      }

      await processDataChunked(lines);

    } catch (error) {
      console.error('Salik import error:', error);
      setProgress({
        phase: 'error',
        currentStep: 0,
        totalSteps: 6,
        message: 'Import failed',
        sessionId,
        details: error.message || 'An unexpected error occurred'
      });
      setIsProcessing(false);
    }
  };

  const checkForDuplicatesOptimized = async (lines: string[]) => {
    try {
      const dateRange = extractDateRangeFromFilename(selectedFile?.name || '');
      const fileHash = await calculateFileHash(lines.join('\n'));
      
      // Quick hash-based duplicate check
      const { data: hashCheck } = await supabase
        .from('accounting_transactions')
        .select('id, import_batch_id')
        .eq('category', 'salik')
        .eq('source_filename', selectedFile?.name || '')
        .limit(1);

      if (hashCheck && hashCheck.length > 0) {
        return {
          hasDuplicates: true,
          isDuplicateFile: true,
          duplicateCount: lines.length - 1
        };
      }

      // Basic date range check for Salik tolls
      const { data: dateCheck } = await supabase
        .from('accounting_transactions')
        .select('id', { count: 'exact', head: true })
        .eq('category', 'salik')
        .gte('transaction_date', dateRange.startDate)
        .lte('transaction_date', dateRange.endDate);

      return {
        hasDuplicates: (dateCheck || 0) > 0,
        isDuplicateFile: false,
        duplicateCount: dateCheck || 0
      };
    } catch (error) {
      console.warn('Duplicate check failed:', error);
      return { hasDuplicates: false, duplicateCount: 0 };
    }
  };

  const processDataChunked = async (lines: string[]) => {
    // Step 4: Vehicle Matching
    setProgress({ 
      phase: 'processing', 
      currentStep: 4, 
      totalSteps: 6, 
      message: 'Matching vehicles...',
      sessionId,
      details: 'Finding vehicles by plate numbers in database'
    });

    const batchId = crypto.randomUUID();
    const fileHash = await calculateFileHash(lines.join('\n'));
    const dateRange = extractDateRangeFromFilename(selectedFile?.name || '');
    
    let processedCount = 0;
    let totalAmount = 0;
    let vehicleMatches = 0;
    let plateMatches = 0;
    let notFoundCount = 0;
    let duplicatesRemoved = 0;

    const chunkSize = 30; // Smaller chunks for Salik due to potential complexity
    const dataLines = lines.slice(1); // Skip header
    const totalRecords = dataLines.length;

    // Step 5: Chunked Data Processing
    setProgress({ 
      phase: 'processing', 
      currentStep: 5, 
      totalSteps: 6, 
      message: 'Processing toll data in chunks...',
      sessionId,
      details: `Processing ${totalRecords} toll records in batches of ${chunkSize}`,
      processed: 0,
      total: totalRecords
    });

    for (let i = 0; i < dataLines.length; i += chunkSize) {
      const chunk = dataLines.slice(i, i + chunkSize);
      const chunkResults = await processChunk(chunk, batchId, fileHash, dateRange, i);
      
      processedCount += chunkResults.processed;
      totalAmount += chunkResults.totalAmount;
      vehicleMatches += chunkResults.vehicleMatches;
      plateMatches += chunkResults.plateMatches;
      notFoundCount += chunkResults.notFound;
      duplicatesRemoved += chunkResults.duplicatesRemoved;

      // Update progress
      setProgress({ 
        phase: 'processing', 
        currentStep: 5, 
        totalSteps: 6, 
        message: 'Processing toll data in chunks...',
        sessionId,
        details: `Processed ${Math.min(i + chunkSize, totalRecords)} of ${totalRecords} toll records`,
        processed: Math.min(i + chunkSize, totalRecords),
        total: totalRecords
      });

      // Save state after each chunk
      await saveSessionState();

      // Small delay to prevent UI blocking
      await new Promise(resolve => setTimeout(resolve, 150));
    }

    // Step 6: Final Database Update
    setProgress({ 
      phase: 'processing', 
      currentStep: 6, 
      totalSteps: 6, 
      message: 'Finalizing import...',
      sessionId,
      details: 'Saving final results'
    });

    const stats: ImportStats = {
      totalRecords,
      vehicleMatches: vehicleMatches,
      plateMatches: plateMatches,
      notFound: notFoundCount,
      duplicatesRemoved,
      totalAmount,
      accuracy: Math.round(((vehicleMatches + plateMatches) / totalRecords) * 100),
      dateRange: `${dateRange.startDate} to ${dateRange.endDate}`,
      batchId: batchId.substring(0, 8),
      sourceFile: selectedFile?.name || 'unknown',
      importType: 'Chunked Processing',
      fileHash: fileHash.substring(0, 16),
      processedRecords: processedCount,
      rawRecordsSaved: totalRecords
    };

    setImportStats(stats);
    setProgress({ 
      phase: 'complete', 
      currentStep: 6, 
      totalSteps: 6, 
      message: 'Salik import completed successfully!',
      sessionId,
      details: `${vehicleMatches + plateMatches} toll transactions imported for period ${stats.dateRange}`
    });
    
    setIsProcessing(false);
    
    // Clear session state
    await storage.removeItem(`salik_import_${sessionId}`);
  };

  const processChunk = async (chunk: string[], batchId: string, fileHash: string, dateRange: any, startIndex: number) => {
    const transactions: any[] = [];
    let processed = 0;
    let totalAmount = 0;
    let vehicleMatches = 0;
    let plateMatches = 0;
    let notFound = 0;
    let duplicatesRemoved = 0;

    for (let i = 0; i < chunk.length; i++) {
      const line = chunk[i].trim();
      if (!line) continue;

      const cells = line.split(',');
      
      // Parse Salik CSV format - Emirates Transport CSV structure
      // Column mapping: Operating Unit(0), Zone(1), Customer(2), Customer Number(3), Contract No(4), 
      // Post date(5), Deduct From(6), Location(7), Direction(8), Plate Number(9), Internal Number(10),
      // Plate Code(11), Plate Source(12), Tag Number(13), Amount(14), Salik Account Number(15), Expense Date(16), Remark(17)
      const plateNumber = cells[9]?.trim() || '';  // Plate Number column
      const tollGate = cells[7]?.trim() || '';     // Location column
      const expenseDateTime = cells[16]?.trim() || ''; // Expense Date column (contains date and time)
      const tollAmount = parseFloat(cells[14]?.trim() || '0'); // Amount column
      const remarkId = cells[17]?.trim() || ''; // Unique remark ID for duplicate prevention
      
      // Extract date and time from expense datetime (format: "01-APR-2025 19:57:57")
      const [tollDate, tollTime] = expenseDateTime.split(' ');
      const transactionDate = parseTollDateTime(tollDate, tollTime);
      
      if (tollAmount > 0 && plateNumber !== '' && remarkId !== '') {
        // Find vehicle by plate number
        const result = await findVehicleByPlateOptimized(plateNumber);
        
        if (result.vehicle) {
          if (result.matchType === 'exact') vehicleMatches++;
          else if (result.matchType === 'partial') plateMatches++;
          
          // Check for duplicates using unique remark ID (most reliable method)
          if (!processedTransactions.has(remarkId)) {
            transactions.push({
              transaction_date: transactionDate,
              type: 'expense',
              category: 'salik',
              amount: tollAmount,
              description: `Salik toll: ${tollGate} - ${plateNumber}`,
              vehicle_id: result.vehicle.id,
              driver_id: result.vehicle.assigned_driver_id || null,
              import_batch_id: batchId,
              batch_start_date: dateRange.startDate,
              batch_end_date: dateRange.endDate,
              source_filename: selectedFile?.name || 'unknown_file',
              session_id: sessionId,
              metadata: JSON.stringify({
                toll_gate: tollGate,
                plate_number: plateNumber,
                toll_time: tollTime,
                expense_datetime: expenseDateTime,
                remark_id: remarkId // Store unique identifier
              })
            });

            processedTransactions.add(remarkId); // Use remark ID for tracking
            totalAmount += tollAmount;
            processed++;
          } else {
            duplicatesRemoved++;
          }
        } else {
          notFound++;
        }
      }
    }

    // Batch insert transactions
    if (transactions.length > 0) {
      const { error: transError } = await supabase
        .from('accounting_transactions')
        .insert(transactions);
      
      if (transError) {
        console.warn('Failed to insert Salik transactions:', transError);
      }
    }

    return {
      processed,
      totalAmount,
      vehicleMatches,
      plateMatches,
      notFound,
      duplicatesRemoved
    };
  };

  // Track processed transactions to avoid duplicates within same import
  const processedTransactions = useRef(new Set());

  // Optimized vehicle lookup with caching
  const vehicleCache = useRef(new Map());
  
  const findVehicleByPlateOptimized = async (plateNumber: string) => {
    const cacheKey = plateNumber.toUpperCase();
    
    if (vehicleCache.current.has(cacheKey)) {
      return vehicleCache.current.get(cacheKey);
    }

    try {
      let result = { vehicle: null, matchType: 'none' };

      // Try exact plate match
      if (plateNumber && plateNumber.trim() !== '') {
        console.log(`Looking for vehicle with plate: "${plateNumber}"`);
        
        const { data: vehicleExact, error: exactError } = await supabase
          .from('vehicles')
          .select('id, license_plate, assigned_driver_id')
          .eq('license_plate', plateNumber.trim().toUpperCase())
          .limit(1);
        
        if (exactError) {
          console.error('Database error during exact match:', exactError);
        }
        
        if (vehicleExact && vehicleExact.length > 0) {
          console.log(`Exact match found:`, vehicleExact[0]);
          result = { vehicle: vehicleExact[0], matchType: 'exact' };
        } else {
          // Try partial match (remove spaces, special characters)
          const cleanPlate = plateNumber.replace(/[^A-Z0-9]/g, '');
          console.log(`Trying partial match with cleaned plate: "${cleanPlate}"`);
          
          const { data: vehiclePartial, error: partialError } = await supabase
            .from('vehicles')
            .select('id, license_plate, assigned_driver_id')
            .ilike('license_plate', `%${cleanPlate}%`)
            .limit(1);
          
          if (partialError) {
            console.error('Database error during partial match:', partialError);
          }
          
          if (vehiclePartial && vehiclePartial.length > 0) {
            console.log(`Partial match found:`, vehiclePartial[0]);
            result = { vehicle: vehiclePartial[0], matchType: 'partial' };
          } else {
            console.log(`No match found for plate: "${plateNumber}"`);
            
            // Debug: Let's see what vehicles exist
            const { data: allVehicles, error: debugError } = await supabase
              .from('vehicles')
              .select('id, license_plate')
              .limit(5);
            
            if (debugError) {
              console.error('Debug query error:', debugError);
            } else {
              console.log('Sample vehicles in database:', allVehicles);
            }
          }
        }
      }

      // Cache the result
      vehicleCache.current.set(cacheKey, result);
      return result;
    } catch (error) {
      console.warn(`Error finding vehicle ${plateNumber}:`, error);
      const result = { vehicle: null, matchType: 'error' };
      vehicleCache.current.set(cacheKey, result);
      return result;
    }
  };

  // Helper functions
  const calculateFileHash = async (content: string): Promise<string> => {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(content);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      return 'hash_failed_' + Date.now();
    }
  };

  const extractDateRangeFromFilename = (filename: string) => {
    const datePattern = /(\d{4}-\d{2}-\d{2})/g;
    const dates = filename.match(datePattern);
    
    if (dates && dates.length >= 2) {
      return { startDate: dates[0], endDate: dates[dates.length - 1] };
    }
    
    // Default to current month for Salik
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    return {
      startDate: startOfMonth.toISOString().split('T')[0],
      endDate: endOfMonth.toISOString().split('T')[0]
    };
  };

  const parseTollDateTime = (date: string, time: string) => {
    try {
      // Parse Salik date format (adjust as needed)
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().split('T')[0];
      }
    } catch (error) {
      console.warn('Date parsing error:', error);
    }
    
    // Fallback to current date
    return new Date().toISOString().split('T')[0];
  };

  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const parseTransactionDate = (dateStr: string) => {
    // Convert "01-APR-2025" to "2025-04-01"
    if (!dateStr) return new Date().toISOString().split('T')[0];
    
    try {
      const monthMap: { [key: string]: string } = {
        'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
        'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
        'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
      };
      
      const [day, month, year] = dateStr.split('-');
      const monthNum = monthMap[month] || '01';
      return `${year}-${monthNum}-${day.padStart(2, '0')}`;
    } catch (error) {
      console.warn('Date parsing error for:', dateStr, error);
      return new Date().toISOString().split('T')[0];
    }
  };

  // Fast bulk import function for large CSV files (25k+ rows)
  const performBulkImport = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setProgress({
      phase: 'processing',
      currentStep: 1,
      totalSteps: 6, // Updated to include duplicate checking
      message: 'Starting super-fast bulk import...',
      sessionId: generateSessionId(),
      details: 'Pre-loading vehicle data for ultra-fast matching'
    });

    try {
      const startTime = Date.now();

      // Step 1: Pre-load all vehicles into memory for fast lookup
      setProgress(prev => ({
        ...prev,
        currentStep: 1,
        message: 'Pre-loading vehicles from database...',
        details: 'Loading all vehicles for instant matching'
      }));

      const { data: vehicles, error: vehicleError } = await supabase
        .from('vehicles')
        .select('id, license_plate, assigned_driver_id')
        .order('license_plate');

      if (vehicleError) throw new Error(`Failed to load vehicles: ${vehicleError.message}`);

      // Create fast lookup map
      const vehicleMap = new Map();
      vehicles?.forEach(vehicle => {
        if (vehicle.license_plate) {
          const cleanPlate = vehicle.license_plate.replace(/\s+/g, '').toLowerCase();
          vehicleMap.set(cleanPlate, vehicle);
        }
      });

      // Step 2: Parse CSV in memory
      setProgress(prev => ({
        ...prev,
        currentStep: 2,
        message: 'Reading and parsing CSV file...',
        details: 'Processing all rows in memory for maximum speed'
      }));

      let content: string;
      if (selectedFile.uri) {
        const response = await fetch(selectedFile.uri);
        content = await response.text();
      } else {
        content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsText(selectedFile as any);
        });
      }
      
      const lines = content.split('\n').filter(line => line.trim());
      const totalLines = lines.length - 1; // Exclude header

      // Step 3: Load existing records for duplicate checking
      setProgress(prev => ({
        ...prev,
        currentStep: 3,
        message: 'Loading existing toll records for duplicate checking...',
        details: 'Fetching all existing remark IDs to prevent duplicates'
      }));

      const { data: existingTolls, error: existingError } = await supabase
        .from('salik_tolls')
        .select('remark');

      if (existingError) {
        console.warn('Warning: Could not load existing tolls for duplicate checking:', existingError.message);
      }

      // Create fast duplicate checking set using unique remark field
      const existingTollsSet = new Set();
      existingTolls?.forEach(toll => {
        if (toll.remark) {
          existingTollsSet.add(toll.remark);
        }
      });

      console.log(`Loaded ${existingTollsSet.size} existing remark IDs for duplicate checking`);

      // Step 4: Process all data in memory with duplicate checking
      setProgress(prev => ({
        ...prev,
        currentStep: 4,
        message: `Processing ${totalLines} records in memory...`,
        details: 'Matching vehicles and checking for duplicate remark IDs'
      }));

      const processedData = [];
      let vehicleMatches = 0;
      let skippedRows = 0;
      const batchId = `BULK_${Date.now()}`;

      for (let i = 1; i < lines.length; i++) {
        const cells = lines[i].split(',');
        
        // Emirates Transport CSV structure
        const plateNumber = cells[9]?.trim().replace(/"/g, '') || '';
        const tollGate = cells[7]?.trim().replace(/"/g, '') || '';
        const amount = parseFloat(cells[14]?.trim().replace(/"/g, '') || '0');
        const expenseDateTime = cells[16]?.trim().replace(/"/g, '') || '';
        const remarkId = cells[17]?.trim().replace(/"/g, '') || ''; // Unique remark ID
        const tagNumber = cells[13]?.trim().replace(/"/g, '') || '';
        const direction = cells[8]?.trim().replace(/"/g, '') || '';
        
        if (!plateNumber || amount <= 0 || !expenseDateTime || !remarkId) {
          skippedRows++;
          continue;
        }

        // Extract date and time
        const [rawDate, transactionTime] = expenseDateTime.split(' ');
        
        // Convert date from "01-APR-2025" to "2025-04-01" format
        const transactionDate = parseTransactionDate(rawDate);
        
        // Check for duplicates using unique remark ID (most reliable method)
        if (existingTollsSet.has(remarkId)) {
          skippedRows++;
          continue; // Skip this duplicate record
        }
        
        // Fast vehicle lookup
        const cleanPlate = plateNumber.replace(/\s+/g, '').toLowerCase();
        const vehicle = vehicleMap.get(cleanPlate);
        if (vehicle) vehicleMatches++;

        processedData.push({
          transaction_date: transactionDate,
          transaction_time: transactionTime || null,
          license_plate: plateNumber,
          toll_gate_name: tollGate || null,
          toll_gate_location: null,
          amount: amount,
          remark: remarkId, // Unique identifier for duplicate prevention
          tag_number: tagNumber || null,
          direction: direction || null,
          expense_date: expenseDateTime, // Keep original format
          vehicle_id: vehicle?.id || null,
          driver_id: vehicle?.assigned_driver_id || null,
          assigned_automatically: !!vehicle,
          assignment_verified: false,
          assignment_confidence: vehicle ? 85 : 0,
          payment_status: 'unpaid',
          processed_to_accounting: false,
          source_file: selectedFile.name,
          batch_reference: batchId
        });
        
        // Add to duplicates set to prevent within-batch duplicates using remark ID
        existingTollsSet.add(remarkId);
      }

      // Step 5: Bulk insert in large batches
      setProgress(prev => ({
        ...prev,
        currentStep: 5,
        message: `Bulk inserting ${processedData.length} records...`,
        details: 'Using optimized batch insertion for maximum speed'
      }));

      const BATCH_SIZE = 100; // Reduced for better error handling
      let insertedCount = 0;
      let errorCount = 0;
      let lastError = null;

      for (let i = 0; i < processedData.length; i += BATCH_SIZE) {
        const batch = processedData.slice(i, i + BATCH_SIZE);
        const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(processedData.length / BATCH_SIZE);

        setProgress(prev => ({
          ...prev,
          message: `Inserting batch ${batchNumber}/${totalBatches}...`,
          details: `Processing ${batch.length} records`,
          processed: insertedCount,
          total: processedData.length
        }));

        const { error } = await supabase
          .from('salik_tolls')
          .insert(batch);

        if (error) {
          console.error(`Batch ${batchNumber} failed:`, error.message);
          console.error('Error details:', error);
          console.error('Sample record from failed batch:', batch[0]);
          lastError = error;
          errorCount += batch.length;
        } else {
          insertedCount += batch.length;
          console.log(`✅ Batch ${batchNumber} completed successfully`);
        }
      }

      // Step 5: Complete and show results
      const endTime = Date.now();
      const totalTime = (endTime - startTime) / 1000;
      const recordsPerSecond = Math.round(insertedCount / totalTime);

      const totalAmount = processedData.reduce((sum, record) => sum + record.amount, 0);

      const stats: ImportStats = {
        totalRecords: processedData.length,
        vehicleMatches: vehicleMatches,
        plateMatches: 0,
        notFound: processedData.length - vehicleMatches,
        totalAmount: totalAmount,
        accuracy: ((vehicleMatches / processedData.length) * 100),
        duplicatesRemoved: skippedRows,
        dateRange: 'Various',
        batchId: batchId,
        sourceFile: selectedFile.name,
        importType: 'BULK_IMPORT',
        processedRecords: insertedCount,
        rawRecordsSaved: insertedCount
      };

      // Step 6: Create company transaction for Salik bill payment
      if (insertedCount > 0 && totalAmount > 0) {
        console.log('💰 Creating company transaction for Salik payment...');
        
        const { error: companyTxError } = await supabase
          .from('company_cash_transactions')
          .insert({
            transaction_date: new Date().toISOString().split('T')[0],
            transaction_type: 'bank_withdrawal',
            category: 'salik_bill_payment',
            amount: totalAmount,
            payment_method: 'bank_transfer',
            reference_number: batchId,
            related_batch_id: batchId,
            verified: false,
            notes: `Salik toll payment - ${insertedCount} transactions imported from ${selectedFile.name}`,
            created_by: null // Will be set by RLS policy
          });

        if (companyTxError) {
          console.error('⚠️ Failed to create company transaction:', companyTxError.message);
        } else {
          console.log('✅ Company transaction created successfully');
        }

        // Create reconciliation record
        const { error: reconError } = await supabase
          .from('batch_reconciliation')
          .insert({
            batch_id: batchId,
            import_type: 'salik',
            import_date: new Date().toISOString(),
            source_file_name: selectedFile.name,
            total_amount_calculated: totalAmount,
            total_amount_from_file: totalAmount,
            difference_amount: 0,
            record_count: insertedCount,
            reconciliation_status: 'matched',
            notes: `Salik import - ${insertedCount} tolls, ${skippedRows} duplicates skipped`,
            reconciled_by: null // Will be set by RLS policy
          });

        if (reconError) {
          console.error('⚠️ Failed to create reconciliation record:', reconError.message);
        } else {
          console.log('✅ Reconciliation record created');
        }
      }

      setImportStats(stats);
      setProgress({
        phase: 'complete',
        currentStep: 6,
        totalSteps: 6,
        message: `Bulk import completed in ${totalTime.toFixed(1)}s!`,
        details: `${recordsPerSecond} records/second • ${insertedCount} imported • ${errorCount} failed • ${skippedRows} duplicates (by remark ID) skipped`,
        sessionId: generateSessionId()
      });

    } catch (error) {
      console.error('Bulk import failed:', error);
      setProgress({
        phase: 'error',
        currentStep: 0,
        totalSteps: 6,
        message: 'Bulk import failed',
        details: error instanceof Error ? error.message : 'Unknown error occurred',
        sessionId: generateSessionId()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setProgress({
      phase: 'idle',
      currentStep: 0,
      totalSteps: 6,
      message: 'Ready to import Salik toll data',
    });
    setImportStats(null);
    setIsProcessing(false);
    vehicleCache.current.clear();
    processedTransactions.current.clear();
  };

  const getStepStatus = (stepNumber: number) => {
    if (progress.currentStep > stepNumber) return 'complete';
    if (progress.currentStep === stepNumber && isProcessing) return 'active';
    return 'pending';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'complete': return theme.colors.success;
      case 'active': return '#1E40AF'; // Salik blue
      default: return theme.colors.gray400;
    }
  };

  const StepIndicator = ({ step, status }: { step: any; status: string }) => (
    <View style={styles.stepItem}>
      <View style={[styles.stepCircle, { borderColor: getStepColor(status) }]}>
        {status === 'complete' ? (
          <Ionicons name="checkmark" size={16} color={theme.colors.success} />
        ) : status === 'active' ? (
          <ActivityIndicator size="small" color="#1E40AF" />
        ) : (
          <Text style={[styles.stepNumber, { color: getStepColor(status) }]}>{step.id}</Text>
        )}
      </View>
      <View style={styles.stepInfo}>
        <Text style={[styles.stepName, { color: getStepColor(status) }]}>{step.name}</Text>
        <Text style={styles.stepDescription}>{step.description}</Text>
      </View>
    </View>
  );

  return (
    <AppLayout title="Salik Import (Fixed)" showBackButton onBackPress={() => navigation.goBack()}>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.header}>
            <View style={[styles.iconContainer, { backgroundColor: '#1E40AF' + '15' }]}>
              <Ionicons name="car-sport" size={24} color="#1E40AF" />
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.title}>Salik Import (Optimized)</Text>
              <Text style={styles.subtitle}>Fixed background processing & toll data chunking</Text>
            </View>
          </View>

          {/* Progress Indicator */}
          {isProcessing && (
            <View style={styles.backgroundWarning}>
              <Ionicons name="shield-checkmark" size={20} color="#1E40AF" />
              <Text style={styles.backgroundText}>
                Safe to minimize app - toll import will continue in background
              </Text>
            </View>
          )}

          {/* File Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>1. Select File</Text>
            <TouchableOpacity 
              style={[styles.filePickerButton, { borderColor: '#1E40AF' }]} 
              onPress={pickFile}
              disabled={isProcessing}
            >
              <Ionicons name="cloud-upload" size={20} color="#1E40AF" />
              <Text style={[styles.filePickerText, { color: '#1E40AF' }]}>
                {selectedFile ? selectedFile.name : 'Choose Salik CSV file'}
              </Text>
            </TouchableOpacity>
            {selectedFile && (
              <View style={styles.fileInfo}>
                <Ionicons name="document-text" size={16} color={theme.colors.success} />
                <Text style={styles.fileInfoText}>
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Text>
              </View>
            )}
          </View>

          {/* Import Method Information */}
          {selectedFile && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Import Methods</Text>
              <View style={styles.methodsInfo}>
                <View style={styles.methodCard}>
                  <View style={[styles.methodIcon, { backgroundColor: '#1E40AF15' }]}>
                    <Ionicons name="shield-checkmark" size={16} color="#1E40AF" />
                  </View>
                  <View style={styles.methodContent}>
                    <Text style={styles.methodTitle}>Standard Import</Text>
                    <Text style={styles.methodDescription}>Full duplicate checking • Small-medium files (&lt;5K rows)</Text>
                  </View>
                </View>
                
                <View style={styles.methodCard}>
                  <View style={[styles.methodIcon, { backgroundColor: '#FF880015' }]}>
                    <Ionicons name="flash" size={16} color="#FF8800" />
                  </View>
                  <View style={styles.methodContent}>
                    <Text style={styles.methodTitle}>Quick Import</Text>
                    <Text style={styles.methodDescription}>Skip duplicates • Medium files (5K-15K rows)</Text>
                  </View>
                </View>

                <View style={styles.methodCard}>
                  <View style={[styles.methodIcon, { backgroundColor: '#DC262615' }]}>
                    <Ionicons name="rocket" size={16} color="#DC2626" />
                  </View>
                  <View style={styles.methodContent}>
                    <Text style={styles.methodTitle}>🚀 Super-Fast Bulk</Text>
                    <Text style={styles.methodDescription}>Memory processing • Large files (25K+ rows) • 100x faster</Text>
                  </View>
                </View>
              </View>
            </View>
          )}

          {/* Progress Steps */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>2. Import Progress</Text>
            <View style={styles.stepsContainer}>
              {steps.map((step) => (
                <StepIndicator 
                  key={step.id} 
                  step={step} 
                  status={getStepStatus(step.id)} 
                />
              ))}
            </View>

            {progress.phase !== 'idle' && (
              <View style={[styles.progressCard, { borderLeftColor: '#1E40AF' }]}>
                <View style={styles.progressHeader}>
                  <Text style={styles.progressMessage}>{progress.message}</Text>
                  {isProcessing && (
                    <ActivityIndicator size="small" color="#1E40AF" />
                  )}
                </View>
                {progress.details && (
                  <Text style={styles.progressDetails}>{progress.details}</Text>
                )}
                {progress.processed && progress.total && (
                  <View style={styles.progressContainer}>
                    <View style={styles.progressBar}>
                      <View 
                        style={[
                          styles.progressFill, 
                          { 
                            width: `${(progress.processed / progress.total) * 100}%`,
                            backgroundColor: '#1E40AF'
                          }
                        ]} 
                      />
                    </View>
                    <Text style={styles.progressText}>
                      {progress.processed}/{progress.total} ({Math.round((progress.processed / progress.total) * 100)}%)
                    </Text>
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Import Results */}
          {importStats && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>3. Import Results</Text>
              <View style={styles.statsContainer}>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{importStats.totalRecords}</Text>
                  <Text style={styles.statLabel}>Total Records</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: '#1E40AF' }]}>
                    {importStats.vehicleMatches}
                  </Text>
                  <Text style={styles.statLabel}>Vehicle Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.warning }]}>
                    {importStats.plateMatches}
                  </Text>
                  <Text style={styles.statLabel}>Plate Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.error }]}>
                    {importStats.notFound}
                  </Text>
                  <Text style={styles.statLabel}>Not Found</Text>
                </View>
              </View>

              <View style={styles.summaryCard}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Total Toll Amount:</Text>
                  <Text style={styles.summaryValue}>
                    AED {importStats.totalAmount.toLocaleString()}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Accuracy:</Text>
                  <Text style={[styles.summaryValue, { color: '#1E40AF' }]}>
                    {importStats.accuracy}%
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Duplicates Removed:</Text>
                  <Text style={styles.summaryValue}>
                    {importStats.duplicatesRemoved}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Batch ID:</Text>
                  <Text style={styles.summaryValue}>
                    {importStats.batchId}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.actionContainer}>
            {!isProcessing ? (
              <>
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#1E40AF' },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performOptimizedImport(false)}
                  disabled={!selectedFile}
                >
                  <Ionicons name="cloud-upload" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>
                    Import with Duplicate Check
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#FF8800', marginTop: 8 },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performOptimizedImport(true)}
                  disabled={!selectedFile}
                >
                  <Ionicons name="flash" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>Quick Import (Skip Duplicates)</Text>
                </TouchableOpacity>

                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#DC2626', marginTop: 8 },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={performBulkImport}
                  disabled={!selectedFile}
                >
                  <Ionicons name="rocket" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>🚀 SUPER-FAST Bulk Import (25k+ rows)</Text>
                </TouchableOpacity>
                
                {progress.phase === 'complete' && (
                  <TouchableOpacity 
                    style={[styles.secondaryButton, { borderColor: '#1E40AF', marginTop: 8 }]} 
                    onPress={resetImport}
                  >
                    <Ionicons name="refresh" size={20} color="#1E40AF" />
                    <Text style={[styles.secondaryButtonText, { color: '#1E40AF' }]}>Reset</Text>
                  </TouchableOpacity>
                )}
              </>
            ) : (
              <View style={styles.processingContainer}>
                <TouchableOpacity style={styles.disabledButton} disabled>
                  <ActivityIndicator size="small" color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>Processing... (Safe to minimize)</Text>
                </TouchableOpacity>
                <Text style={styles.processingNote}>
                  Toll import will continue in background. You can safely switch to other apps.
                </Text>
              </View>
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  backgroundWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: '#1E40AF' + '10',
    borderWidth: 1,
    borderColor: '#1E40AF' + '30',
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.sm,
  },
  backgroundText: {
    fontSize: theme.typography.sizes.sm,
    color: '#1E40AF',
    fontWeight: '500' as any,
  },
  section: {
    backgroundColor: theme.colors.white,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  filePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: theme.borderRadius.lg,
    backgroundColor: '#1E40AF' + '05',
    gap: theme.spacing.sm,
  },
  filePickerText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
  },
  fileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.success + '10',
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  fileInfoText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.success,
  },
  stepsContainer: {
    gap: theme.spacing.md,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  stepNumber: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
  },
  stepInfo: {
    flex: 1,
  },
  stepName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    marginBottom: 2,
  },
  stepDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  progressCard: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    borderLeftWidth: 4,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  progressMessage: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  progressDetails: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  progressContainer: {
    gap: theme.spacing.xs,
  },
  progressBar: {
    height: 6,
    backgroundColor: theme.colors.gray200,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  progressText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  statsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
  },
  statNumber: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  summaryCard: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  summaryValue: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  actionContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  primaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    gap: theme.spacing.sm,
  },
  secondaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
  },
  disabledButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.gray400,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  processingContainer: {
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  processingNote: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  methodsInfo: {
    gap: theme.spacing.sm,
  },
  methodCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    gap: theme.spacing.md,
  },
  methodIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  methodContent: {
    flex: 1,
  },
  methodTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  methodDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
});

export default SalikImportScreenFixed;
